import { ReactNode } from 'react';
import { useReportEditing } from './ReportEditing';
import { useOutlineStore } from './Outline';
import { useSheetSection } from './Sheet';

interface ReportFieldProps {
  id: string;
  label: string;
  value: string;
  onChange?: (value: string) => void;
  choices?: ReadonlyArray<string>;
  choiceLabels?: Record<string, string>;
  multiline?: boolean;
  optional?: boolean;
  /** rendered after the value when read-only, e.g. who answered */
  by?: ReactNode;
  /** a hint under the label while it is filled in */
  help?: string;
}

/**
 * A report-time field inside the document: an input while the owner fills
 * the report in, the recorded value (or "not recorded") once published.
 */
export function ReportField({ id, label, value, onChange, choices, choiceLabels, multiline, optional, by, help }: ReportFieldProps) {
  const { readOnly } = useReportEditing();
  const editable = !readOnly && onChange;
  const display = value ? (choiceLabels?.[value] ?? value) : null;
  const outline = useOutlineStore();
  const section = useSheetSection();
  if (editable) outline?.add({ id, label, kind: 'field', filled: value.trim() !== '', optional: !!optional, section: section?.title ?? null });

  return (
    <div className="entry">
      <label className="entry-label" htmlFor={id}>{label}{optional ? ' (optional)' : ''}</label>
      {editable ? (
        choices ? (
          <select id={id} className="fill-select" value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
            <option value="">—</option>
            {choices.map((c) => <option key={c} value={c}>{choiceLabels?.[c] ?? c}</option>)}
          </select>
        ) : multiline ? (
          <textarea id={id} className="fill-textarea" value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} />
        ) : (
          <input id={id} className="fill-input" value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} />
        )
      ) : (
        <span className="entry-value" id={id}>{display ?? <span className="unset">not recorded</span>}</span>
      )}
      {by && <span className="entry-by">{by}</span>}
      {editable && help && <span className="entry-help">{help}</span>}
    </div>
  );
}

interface SignoffProps {
  id: string;
  role: string;
  covers?: string;
  value: string;
  onChange?: (value: string) => void;
  /** the line may stay blank; the outline doesn't count it as left to do */
  optional?: boolean;
}

/** The sign-line: dashed until a name is typed on it. */
export function Signoff({ id, role, covers, value, onChange, optional }: SignoffProps) {
  const { readOnly } = useReportEditing();
  const editable = !readOnly && onChange;
  const outline = useOutlineStore();
  const section = useSheetSection();
  if (editable) outline?.add({ id, label: role, kind: 'signoff', filled: value.trim() !== '', optional: !!optional, section: section?.title ?? null });
  return (
    <div className={`signoff ${value ? '' : 'pending'}`}>
      <span className="signoff-mark" aria-hidden>✎</span>
      <div style={{ flex: 1 }}>
        <div>
          <span className="entry-label" style={{ minWidth: 0, marginRight: 8 }}>{role}{optional ? ' (optional)' : ''}</span>
          {editable ? (
            <input id={id} className="fill-input" value={value} onChange={(e) => onChange(e.target.value)} placeholder="Signed by" aria-label={`${role} sign-off`} />
          ) : (
            <span className="entry-value">{value || <span className="unset">unsigned</span>}</span>
          )}
        </div>
        {covers && <div className="signoff-meta">covers {covers}</div>}
      </div>
    </div>
  );
}
